import React from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";

export default function MobileProfile() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  const roleKey =
    user?.role === "shop_owner"
      ? "profile.owner"
      : user?.role === "admin"
      ? "profile.admin"
      : "profile.customer";

  const approvalKey =
    user?.approval_status === "pending" ? "profile.pending" : "profile.approved";

  const handleSignOut = () => {
    logout();
    nav("/", { replace: true });
  };

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("profile.title")}</h1>
        <MenuButton />
      </div>

      <div className="h-px bg-border my-5" />

      {/* Profile card */}
      <div
        data-testid="profile-card"
        className="bg-secondary/50 border border-border rounded-3xl p-8 text-center"
      >
        <span className="grid place-items-center w-24 h-24 rounded-full bg-primary text-primary-foreground text-4xl font-extrabold mx-auto">
          {initial}
        </span>
        <p className="font-display font-extrabold text-2xl mt-4">{user?.name || "—"}</p>
        <p className="text-muted-foreground text-sm mt-1 break-all">{user?.email || ""}</p>
        <div className="mt-4 flex flex-col items-center gap-2">
          <span className="text-xs font-extrabold tracking-widest bg-[#E7F3E5] text-[#3B7A2E] rounded-full px-3 py-1">
            {t(roleKey)}
          </span>
          <span className="text-xs font-extrabold tracking-widest bg-[#E7F3E5] text-[#3B7A2E] rounded-full px-3 py-1">
            {t(approvalKey)}
          </span>
        </div>
      </div>

      {/* Language toggle */}
      <div className="mt-4 bg-card border border-border rounded-2xl p-4" data-testid="language-block">
        <p className="flex items-center gap-2 font-bold text-lg mb-3">
          <Languages className="w-5 h-5 text-primary" /> {t("profile.language")}
        </p>
        <div className="flex items-center gap-2">
          <LangBtn active={lang === "en"} onClick={() => setLang("en")} label={t("profile.english")} check testid="lang-en" />
          <LangBtn active={lang === "ml"} onClick={() => setLang("ml")} label={t("profile.malayalam")} testid="lang-ml" />
        </div>
      </div>

      {/* Sign out */}
      <button
        data-testid="signout-btn"
        onClick={handleSignOut}
        className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl bg-primary text-primary-foreground font-bold py-4 hover:bg-primary/90 transition-colors"
      >
        <LogOut className="w-5 h-5" /> {t("profile.signOut")}
      </button>
    </div>
  );
}

function LangBtn({ active, onClick, label, check, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-semibold text-sm transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "bg-secondary text-foreground hover:bg-secondary/70"
      }`}
    >
      {active && check && (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {label}
    </button>
  );
}
